import { ADMIN_BASE_PATH, ADMIN_CHANGE_PASSWORD_PATH, ADMIN_DASHBOARD_PATH, ADMIN_SETUP_2FA_PATH } from '@/lib/admin-paths';
import { authDebugEnabled } from '@/lib/auth-debug';
import { traceLogin } from '@/lib/login-redirect-trace';
import { buildParticipantUrl } from './participant-links';
import {
  getEffectiveRole,
  isAdminRole,
  isJuryRole,
  isOrganizationAdminRole,
  isOrganizationStaffRole,
  isOrganizerRole,
  isParticipantRole,
} from '@/lib/rbac';

export const USE_ROLE_REDIRECTS =
  process.env.NEXT_PUBLIC_USE_ROLE_REDIRECTS !== 'false';

export type RoleDashboardUser = {
  role?: string;
  user_type?: string;
  roles?: Array<string | { name?: string }>;
  master_admin?: boolean;
  must_change_password?: boolean;
  two_factor?: { setup_required?: boolean };
  approval_status?: string;
  is_approval_pending?: boolean;
  is_approval_rejected?: boolean;
};

export const ADMIN_DASHBOARD_ZONE = '/dashboard/admin';
export const ORGANIZATION_DASHBOARD_PATH = '/dashboard/organization';
export const ORGANIZER_DASHBOARD_PATH = '/dashboard/organizer';
export const PARTICIPANT_DASHBOARD_PATH = '/dashboard';
export const JURY_DASHBOARD_PATH = '/jury';
export const SCANNER_DASHBOARD_PATH = '/scanner';
export const DEFAULT_DASHBOARD_PATH = PARTICIPANT_DASHBOARD_PATH;

const ORGANIZER_DASHBOARD_ALIASES = new Set([
  'organizer',
  'individual_organizer',
  'event_manager',
  'team_manager',
]);

/** Personal attendee routes any authenticated role may use (tickets, passes, etc.). */
export const PERSONAL_PARTICIPANT_DASHBOARD_PREFIXES = [
  '/dashboard/my-tickets',
  '/dashboard/my-passes',
  '/dashboard/my-events',
] as const;

export function isPersonalParticipantDashboardPath(path: string): boolean {
  const normalized = normalizePath(path);
  return PERSONAL_PARTICIPANT_DASHBOARD_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`)
  );
}

const ORG_ADMIN_SHARED_DASHBOARD_PREFIXES = [
  '/dashboard/events',
  '/dashboard/forms',
  '/dashboard/calendar',
  '/dashboard/organization/analytics',
  '/dashboard/analytics',
  '/dashboard/wallet',
  '/dashboard/payouts',
  '/dashboard/notifications',
  '/dashboard/support',
  '/dashboard/endorsements',
  '/dashboard/participants',
  '/dashboard/settings',
  '/dashboard/profile',
  '/dashboard/developer',
];

const ORG_MANAGER_SHARED_DASHBOARD_PREFIXES = [
  ...PERSONAL_PARTICIPANT_DASHBOARD_PREFIXES,
  '/dashboard/events',
  '/dashboard/forms',
  '/dashboard/calendar',
  '/dashboard/organization/analytics',
  '/dashboard/notifications',
  '/dashboard/support',
];

function isOrgAdminSharedDashboardPath(path: string): boolean {
  return ORG_ADMIN_SHARED_DASHBOARD_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
}

function isOrgManagerSharedDashboardPath(path: string): boolean {
  return ORG_MANAGER_SHARED_DASHBOARD_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
}

function normalizePath(path: string): string {
  return path.split('?')[0].replace(/\/$/, '') || '/';
}

function isOrganizerDashboardRole(role: string): boolean {
  return isOrganizerRole(role) || ORGANIZER_DASHBOARD_ALIASES.has(role);
}

function logLoginRedirect(payload: Record<string, unknown>): void {
  if (!authDebugEnabled()) return;
  console.info('login_redirect', payload);
}

/** Default home dashboard for a user based on role (and master_admin flag). */
export function resolveDashboardForUser(user: RoleDashboardUser | null | undefined): string {
  if (!user) return DEFAULT_DASHBOARD_PATH;

  const role = getEffectiveRole(user);

  if (user.master_admin || isAdminRole(role)) {
    return ADMIN_DASHBOARD_PATH;
  }

  if (isOrganizationStaffRole(role)) {
    return ORGANIZATION_DASHBOARD_PATH;
  }

  if (isOrganizerDashboardRole(role)) {
    const approvalStatus = String(user?.approval_status || '').toUpperCase();
    const isPending = approvalStatus === 'PENDING' || Boolean(user?.is_approval_pending);
    const isRejected = approvalStatus === 'REJECTED' || Boolean(user?.is_approval_rejected);
    if (!isPending && !isRejected) {
      return ORGANIZER_DASHBOARD_PATH;
    }
  }

  if (isJuryRole(role)) {
    return JURY_DASHBOARD_PATH;
  }

  if (role === 'scanner') {
    return SCANNER_DASHBOARD_PATH;
  }

  return DEFAULT_DASHBOARD_PATH;
}

function isAdminZonePath(path: string): boolean {
  return path === ADMIN_BASE_PATH || path.startsWith(`${ADMIN_BASE_PATH}/`) || path.startsWith('/admin');
}

function isOrganizationZonePath(path: string): boolean {
  return (
    path === ORGANIZATION_DASHBOARD_PATH ||
    path.startsWith(`${ORGANIZATION_DASHBOARD_PATH}/`) ||
    path.startsWith('/organization')
  );
}

function isOrganizerZonePath(path: string): boolean {
  if (path === ORGANIZER_DASHBOARD_PATH || path.startsWith(`${ORGANIZER_DASHBOARD_PATH}/`)) {
    return true;
  }
  if (!path.startsWith('/dashboard')) return false;
  if (isAdminZonePath(path) || isOrganizationZonePath(path)) return false;
  if (path.startsWith('/dashboard/events')) return false;
  return true;
}

/** Whether an explicit post-login redirect is safe for this user. */
export function isRedirectAllowedForUser(user: RoleDashboardUser | null | undefined, path: string): boolean {
  if (!user || !path?.startsWith('/')) return false;

  const normalized = normalizePath(path);
  const role = getEffectiveRole(user);
  const isAdmin = user.master_admin || isAdminRole(role);

  if (isAdminZonePath(normalized)) {
    return isAdmin;
  }

  if (normalized.startsWith('/jury')) {
    return isAdmin || isJuryRole(role);
  }

  if (normalized.startsWith('/scanner')) {
    return role === 'scanner';
  }

  if (isOrganizationZonePath(normalized)) {
    return isOrganizationStaffRole(role);
  }

  if (normalized.startsWith('/organizer')) {
    const approvalStatus = String(user?.approval_status || '').toUpperCase();
    const isPending = approvalStatus === 'PENDING' || Boolean(user?.is_approval_pending);
    const isRejected = approvalStatus === 'REJECTED' || Boolean(user?.is_approval_rejected);
    if (isPending || isRejected) return false;
    return isOrganizerDashboardRole(role);
  }

  if (normalized.startsWith('/dashboard')) {
    if (isAdmin) return false;
    if (isOrganizationStaffRole(role)) {
      if (isOrganizationAdminRole(role)) {
        return (
          normalized === '/dashboard' ||
          isOrganizationZonePath(normalized) ||
          isOrgAdminSharedDashboardPath(normalized) ||
          isPersonalParticipantDashboardPath(normalized) ||
          normalized.startsWith('/dashboard/events') ||
          normalized.startsWith('/dashboard/organizer')
        );
      }
      return (
        normalized === '/dashboard' ||
        isOrganizationZonePath(normalized) ||
        isOrgManagerSharedDashboardPath(normalized) ||
        normalized.startsWith('/dashboard/events') ||
        normalized.startsWith('/dashboard/organizer')
      );
    }
    if (isOrganizerDashboardRole(role)) {
      const approvalStatus = String(user?.approval_status || '').toUpperCase();
      const isPending = approvalStatus === 'PENDING' || Boolean(user?.is_approval_pending);
      const isRejected = approvalStatus === 'REJECTED' || Boolean(user?.is_approval_rejected);
      if (isPending || isRejected) {
        return !normalized.startsWith('/dashboard/organizer') && !isJuryRole(role);
      }
      return (
        isOrganizerZonePath(normalized) ||
        normalized.startsWith('/dashboard/events') ||
        normalized.startsWith('/dashboard/forms')
      );
    }
    if (normalized === ORGANIZER_DASHBOARD_PATH || normalized.startsWith(`${ORGANIZER_DASHBOARD_PATH}/`)) {
      return false;
    }
    return !isJuryRole(role);
  }

  if (isAdmin) {
    return false;
  }

  return true;
}

export type PostLoginDestinationOptions = {
  redirectPath?: string | null;
};

/**
 * Resolves where to send the user after authentication.
 * Priority: 2FA setup (admins) → validated redirect param → role dashboard → fallback.
 */
export function resolvePostLoginDestination(
  user: RoleDashboardUser | null | undefined,
  options?: PostLoginDestinationOptions
): string {
  const userId = (user as { id?: string } | null | undefined)?.id ?? null;
  const redirectParam = options?.redirectPath?.trim() || null;

  traceLogin('resolvePostLoginDestination CALLED', {
    userId,
    role: user ? getEffectiveRole(user) : null,
    redirectParam,
  });

  if (!user) {
    traceLogin('resolvePostLoginDestination RESULT', { destination: '/login', reason: 'no_user' });
    return '/login';
  }

  const role = getEffectiveRole(user);
  if (!role) {
    console.error('Missing role in user object', user);
    traceLogin('resolvePostLoginDestination RESULT', { destination: '/login', reason: 'no_role' });
    return '/login';
  }

  const isAdmin = user.master_admin || isAdminRole(role);
  if (!USE_ROLE_REDIRECTS && isAdmin) {
    const finalDestination = user.two_factor?.setup_required
      ? ADMIN_SETUP_2FA_PATH
      : user.must_change_password
        ? ADMIN_CHANGE_PASSWORD_PATH
        : ADMIN_DASHBOARD_PATH;
    logLoginRedirect({
      role,
      redirectParam,
      resolved: ADMIN_DASHBOARD_PATH,
      finalDestination,
      useRoleRedirects: false,
    });
    traceLogin('resolvePostLoginDestination RESULT', {
      destination: finalDestination,
      role,
      userId,
      reason: 'admin_legacy_redirects',
    });
    return finalDestination;
  }

  if (isAdmin && user.two_factor?.setup_required) {
    logLoginRedirect({
      role,
      redirectParam,
      resolved: ADMIN_SETUP_2FA_PATH,
      finalDestination: ADMIN_SETUP_2FA_PATH,
      reason: 'admin_2fa_setup_required',
    });
    traceLogin('resolvePostLoginDestination RESULT', {
      destination: ADMIN_SETUP_2FA_PATH,
      role,
      userId,
      reason: 'admin_2fa_setup_required',
    });
    return ADMIN_SETUP_2FA_PATH;
  }

  if (isAdmin && user.must_change_password) {
    logLoginRedirect({
      role,
      redirectParam,
      resolved: ADMIN_CHANGE_PASSWORD_PATH,
      finalDestination: ADMIN_CHANGE_PASSWORD_PATH,
      reason: 'admin_must_change_password',
    });
    traceLogin('resolvePostLoginDestination RESULT', {
      destination: ADMIN_CHANGE_PASSWORD_PATH,
      role,
      userId,
      reason: 'admin_must_change_password',
    });
    return ADMIN_CHANGE_PASSWORD_PATH;
  }

  if (!isAdmin && user.must_change_password) {
    const destination = '/set-password';
    logLoginRedirect({
      role,
      redirectParam,
      resolved: destination,
      finalDestination: destination,
      reason: 'must_change_password',
    });
    return destination;
  }

  const roleDashboard = resolveDashboardForUser(user);

  if (redirectParam && isRedirectAllowedForUser(user, redirectParam)) {
    const finalDestination = normalizePath(redirectParam);
    logLoginRedirect({ role, redirectParam, resolved: roleDashboard, finalDestination });
    traceLogin('resolvePostLoginDestination RESULT', {
      destination: finalDestination,
      role,
      userId,
      redirectParam,
      reason: 'redirect_param_allowed',
    });
    return finalDestination;
  }

  logLoginRedirect({
    role,
    redirectParam,
    resolved: roleDashboard,
    finalDestination: roleDashboard,
    redirectIgnored: Boolean(redirectParam && !isRedirectAllowedForUser(user, redirectParam)),
  });
  traceLogin('resolvePostLoginDestination RESULT', {
    destination: roleDashboard,
    role,
    userId,
    redirectParam,
    redirectIgnored: Boolean(redirectParam && !isRedirectAllowedForUser(user, redirectParam)),
    reason: 'role_dashboard',
  });
  return roleDashboard;
}

/** Redirect when user is in the wrong route zone for their role (avoids loops). */
export function resolveRoleZoneRedirect(
  pathname: string | null | undefined,
  user: RoleDashboardUser | null | undefined
): string | null {
  if (!pathname || !user) return null;

  const role = getEffectiveRole(user);
  const current = normalizePath(pathname);
  const isAdmin = user.master_admin || isAdminRole(role);

  if (user.must_change_password) {
    const required = isAdmin ? ADMIN_CHANGE_PASSWORD_PATH : '/set-password';
    if (current !== normalizePath(required)) {
      return required;
    }
    return null;
  }

  const destination = resolveDashboardForUser(user);
  const target = normalizePath(destination);

  if (current === target) {
    return null;
  }

  if (isAdmin && (current === '/dashboard' || current.startsWith('/dashboard/'))) {
    // Keep dual-role admins on event workspace; bounce platform-only admins to admin home.
    if (current.startsWith('/dashboard/events')) {
      const roles = Array.isArray(user.roles) ? user.roles : [];
      const dualRole = roles.some(
        (r) =>
          r === 'organizer' ||
          r === 'individual_organizer' ||
          r === 'organization_admin' ||
          r === 'organization_manager'
      );
      if (dualRole || isOrganizerDashboardRole(role) || isOrganizationStaffRole(role)) {
        return null;
      }
      return destination;
    }
    if (isAdminZonePath(current)) return null;
    return destination;
  }

  if (!isAdmin && isAdminZonePath(current)) {
    return destination;
  }

  if (isOrganizationStaffRole(role)) {
    if (current === '/dashboard' || current === ORGANIZER_DASHBOARD_PATH) {
      return ORGANIZATION_DASHBOARD_PATH;
    }
    const sharedPaths = isOrganizationAdminRole(role)
      ? isOrgAdminSharedDashboardPath(current) || isPersonalParticipantDashboardPath(current)
      : isOrgManagerSharedDashboardPath(current);
    if (
      current.startsWith('/dashboard/') &&
      !isOrganizationZonePath(current) &&
      !sharedPaths
    ) {
      return ORGANIZATION_DASHBOARD_PATH;
    }
    if (current.startsWith('/organization')) {
      return ORGANIZATION_DASHBOARD_PATH;
    }
  }

  if (isOrganizerDashboardRole(role) && !isOrganizationStaffRole(role)) {
    if (current === '/dashboard') {
      return ORGANIZER_DASHBOARD_PATH;
    }
    if (current.startsWith('/organization')) {
      return ORGANIZER_DASHBOARD_PATH;
    }
  }

  if (isJuryRole(role)) {
    if (current === '/dashboard' || (current.startsWith('/dashboard/') && !current.startsWith('/dashboard/events'))) {
      return JURY_DASHBOARD_PATH;
    }
  }

  if (
    isParticipantRole(role) &&
    !isAdmin &&
    !isOrganizerRole(role) &&
    !isOrganizationStaffRole(role) &&
    !isJuryRole(role)
  ) {
    if (current.startsWith('/dashboard/events/')) {
      const parts = current.split('/').filter(Boolean);
      const evIdx = parts.indexOf('events');
      const actIdx = parts.indexOf('activities');
      if (evIdx >= 0 && actIdx > evIdx) {
        const evId = parts[evIdx + 1];
        const actId = parts[actIdx + 1];
        const directUrl = buildParticipantUrl(evId, actId);
        if (directUrl) return directUrl;
      }
      return PARTICIPANT_DASHBOARD_PATH;
    }
  }

  if (!isAdmin && current.startsWith('/admin')) {
    return destination;
  }

  if (
    current.startsWith(`${target}/`) &&
    !isAdminZonePath(current) &&
    !isOrganizationZonePath(current)
  ) {
    return null;
  }

  return null;
}
